import React, { useMemo } from "react";
import Editor from "@monaco-editor/react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { CopyToClipboard } from "../../common/ProjectIcons";
import {
    CODE_LANGUAGES,
    generateSnippet,
    languageLabel,
    monacoLanguageFor,
    SnippetKind,
} from "./snippets";
import { usePreferredCodeLanguage } from "./usePreferredCodeLanguage";
import { MONO_FONT_FAMILY } from "../../../fonts";

const TOAST_DURATION_MS = 2000;
const EDITOR_FONT_SIZE = 13;
const EDITOR_OPTIONS = {
    readOnly: true,
    fontFamily: MONO_FONT_FAMILY,
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    fontSize: EDITOR_FONT_SIZE,
    lineNumbers: "off" as const,
    renderLineHighlight: "none" as const,
    scrollbar: { vertical: "hidden" as const, horizontal: "hidden" as const, alwaysConsumeMouseWheel: false },
    overviewRulerLanes: 0,
    folding: false,
    wordWrap: "on" as const,
};

const pillBase = "px-3 py-1.5 text-sm rounded-full cursor-pointer transition-colors duration-200";
const pillActive = `${pillBase} bg-dark-green text-white`;
const pillInactive = `${pillBase} bg-light-gray text-secondary-gray hover:text-off-black`;

type CodeSampleSwitcherProps = {
    endpoint: string;
    apiKey: string | null;
    integrationType: string;
    kind: SnippetKind;
    height: string;
};

const CodeSampleSwitcher = ({ endpoint, apiKey, integrationType, kind, height }: CodeSampleSwitcherProps) => {
    const { t } = useTranslation();
    const [language, setLanguage] = usePreferredCodeLanguage();

    const snippet = useMemo(
        () => generateSnippet(language, { endpoint, apiKey, integrationType, kind }),
        [language, endpoint, apiKey, integrationType, kind],
    );

    return (
        <div className="w-full">
            {apiKey !== null && (
                <p className="mb-2 max-w-[765px] text-sm bg-warning-yellow text-warning-dark-yellow rounded px-3 py-2">
                    {t("This code contains your real API key. Avoid sharing your screen while this is visible.")}
                </p>
            )}
            <div
                role="group"
                aria-label={t("Code language")}
                className="flex flex-row flex-wrap gap-2 mb-3"
            >
                {CODE_LANGUAGES.map((lang) => {
                    const isActive = lang === language;
                    return (
                        <button
                            key={lang}
                            type="button"
                            aria-pressed={isActive}
                            onClick={() => setLanguage(lang)}
                            className={isActive ? pillActive : pillInactive}
                        >
                            {t(languageLabel(lang))}
                        </button>
                    );
                })}
                <button
                    type="button"
                    onClick={() => {
                        navigator.clipboard.writeText(snippet)
                            .then(() => toast.info(t("Code sample copied to clipboard"), { position: toast.POSITION.TOP_RIGHT, autoClose: TOAST_DURATION_MS }))
                            .catch(() => toast.error(t("Failed to copy to clipboard"), { position: toast.POSITION.TOP_RIGHT, autoClose: TOAST_DURATION_MS }));
                    }}
                    className="ml-auto text-secondary-gray hover:text-off-black flex-shrink-0"
                    aria-label={t("Copy code sample")}
                >
                    <CopyToClipboard />
                </button>
            </div>
            <div className="rounded overflow-hidden">
                <Editor
                    height={height}
                    language={monacoLanguageFor(language)}
                    theme="vs"
                    value={snippet}
                    options={EDITOR_OPTIONS}
                />
            </div>
        </div>
    );
};

export default CodeSampleSwitcher;
